import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import HelpModal from '@shared/ui/HelpModal';

interface SyntaxExample {
  query: string;
  description: string;
  explanation: string;
}

interface AdvancedSearchHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

const syntaxExamples: SyntaxExample[] = [
  {
    query: '"affordable housing"',
    description: 'Exact phrase matching',
    explanation: 'Finds bills containing the exact phrase "affordable housing"'
  },
  {
    query: 'housing -education',
    description: 'Exclusion search',
    explanation: 'Finds bills with "housing" but excludes those also containing "education"'
  },
  {
    query: 'housing education funding',
    description: 'Multiple terms (AND)',
    explanation: 'Finds bills containing all three terms: housing AND education AND funding'
  },
  {
    query: 'housing OR education funding',
    description: 'OR logic',
    explanation: 'Finds bills with (housing OR education) AND funding'
  },
  {
    query: '"mental health" OR "behavioral health"',
    description: 'Complex OR',
    explanation: 'Finds bills with exact phrase "mental health" OR exact phrase "behavioral health"'
  },
  {
    query: '"mental health" -criminal',
    description: 'Complex search',
    explanation: 'Finds bills with exact phrase "mental health" but excludes those containing "criminal"'
  }
];

const AdvancedSearchHelp: React.FC<AdvancedSearchHelpProps> = ({ isOpen, onClose }) => {
  const [copiedQuery, setCopiedQuery] = useState<string | null>(null);

  const copyToClipboard = async (query: string) => {
    try {
      await navigator.clipboard.writeText(query);
      setCopiedQuery(query);
      setTimeout(() => setCopiedQuery(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    // `lg` rather than the default `sm`: this one carries a syntax reference, not
    // a paragraph, and the code examples need the width.
    <HelpModal isOpen={isOpen} title="Advanced search syntax" onClose={onClose} size="lg" closeLabel="Got it">
          {/* Introduction */}
          <div className="mb-6">
            <p className="text-text-secondary dark:text-text-dark-secondary mb-4">
              Use advanced search syntax to create more precise searches across bills.
              These syntax features work alongside the existing filter system.
            </p>
          </div>

          {/* Syntax Examples */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Search Syntax Examples
            </h3>
            
            {syntaxExamples.map((example, index) => (
              <div key={index} className="border border-border-light dark:border-border-dark-hairline rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <code className="bg-background-tertiary dark:bg-background-dark-tertiary px-2 py-1 rounded text-sm font-mono text-blue-600 dark:text-blue-400">
                        {example.query}
                      </code>
                      <span className="text-sm font-medium text-neutral-600 dark:text-text-dark-tertiary">
                        {example.description}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-text-dark-tertiary">
                      {example.explanation}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(example.query)}
                    className="ml-3 p-2 text-neutral-500 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-text-dark-secondary transition-colors"
                  >
                    {copiedQuery === example.query ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Syntax Rules */}
          <div className="mt-8">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Syntax Rules
            </h3>
            <div className="bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <span className="text-blue-500 font-mono text-sm mt-1">{'"phrase"'}</span>
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                    Exact phrase matching
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                    Use quotes to search for exact phrase matches. Words must appear together in exact order.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="text-red-500 font-mono text-sm mt-1">-word</span>
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                    Exclusion terms
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                    Prefix with dash (-) to exclude bills containing that word or phrase.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="text-green-500 font-mono text-sm mt-1">word1 word2 word3</span>
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                    Multiple terms (AND logic)
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                    Separate words with spaces to require ALL terms to be present (AND logic).
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <span className="text-orange-500 font-mono text-sm mt-1">word1 OR word2</span>
                <div>
                  <p className="text-sm font-medium text-neutral-700 dark:text-text-dark-secondary">
                    OR logic
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-text-dark-tertiary">
                    {`Use "OR" (case insensitive) to find bills with ANY of the terms (OR logic).`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search Zones */}
          <div className="mt-8">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              What Gets Searched
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-neutral-600 dark:text-text-dark-tertiary">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-400 mb-1">Bill Numbers</h4>
                <p>HB25-1333, SB25-1040, etc.</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                <h4 className="font-medium text-green-900 dark:text-green-400 mb-1">Bill Titles</h4>
                <p>Full bill titles and descriptions</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                <h4 className="font-medium text-purple-900 dark:text-purple-400 mb-1">Bill Summaries</h4>
                <p>Detailed bill content and text</p>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="mt-8">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
              Pro Tips
            </h3>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 space-y-2 text-sm text-yellow-800 dark:text-yellow-200">
              <div className="flex items-start space-x-2">
                <span className="font-semibold">💡</span>
                <p>Combine with filters for powerful searches. Advanced syntax works in the search box, filters work independently.</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold">💡</span>
                <p>{`Use exclusions (-) to refine broad searches. Try "housing -education" instead of just "housing".`}</p>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-semibold">💡</span>
                <p>Save successful searches as filters to reuse complex search combinations.</p>
              </div>
            </div>
          </div>
    </HelpModal>
  );
};

export default AdvancedSearchHelp;
